import { useCallback, useId, useMemo, useState } from 'react';
import type { Rule } from '@/types/entities';
import type { ConditionType } from '@/types/condition';
import { Button } from '@/components/primitives/Button';
import { ConditionBadge } from '@/components/primitives/ConditionBadge';
import { InfoBanner } from '@/components/primitives/InfoBanner';
import { StatusDot } from '@/components/primitives/StatusDot';
import { useDeleteRule, useUpdateRule } from '@/hooks/useRules';
import styles from './RulePriorityRow.module.css';

// ── Backend / form mapping helpers ──────────────────────────────────────────
//
// The inline condition editor speaks the lower-case backend field names
// (`app`, `title`, `url`, `site`, `document`, `ip`) and the three UI
// operator labels (`contains`, `is`, `matches`) that AddRulePanel also
// speaks. The Rule type carries the upper-case ConditionType the read
// path uses, so we map both directions when seeding form state and when
// shipping the PATCH body.

type FormConditionField = 'app' | 'title' | 'url' | 'site' | 'document' | 'ip';
type FormConditionOperator = 'contains' | 'is' | 'matches';
type ApiMatchType = 'pattern' | 'regex' | 'cidr';

// Inverse of FIELD_TO_TYPE in useRules.ts. Co-located here because
// RulePriorityRow is the only forward-mapping caller for the inline edit
// flow today (AddRulePanel doesn't seed from a Rule, so its only reverse
// helper is operator → match-type).
const FIELD_TO_BACKEND: Record<ConditionType, FormConditionField> = {
  APP: 'app',
  TITLE: 'title',
  URL: 'url',
  SITE: 'site',
  IP: 'ip',
  DOCUMENT: 'document',
};

// Same shape as AddRulePanel's `operatorToMatchType` (D7: starts with /
// ends with are intentionally absent — neither has a backend
// representation, and the inline operator dropdown no longer offers
// them).
function operatorToMatchType(operator: FormConditionOperator): ApiMatchType {
  if (operator === 'matches') return 'regex';
  if (operator === 'is') return 'cidr';
  return 'pattern';
}

// Coerce the read-side `RuleCondition.operator` (which is the union of
// all five legacy labels) into the three the inline editor accepts.
// `starts with` and `ends with` cannot survive a round-trip — neither
// has a backend representation — so a rule that somehow carries them
// (legacy seed data, manual DB edit) collapses to `contains` on edit.
function coerceOperator(op: string): FormConditionOperator {
  if (op === 'matches') return 'matches';
  if (op === 'is') return 'is';
  return 'contains';
}

interface FormCondition {
  field: FormConditionField;
  operator: FormConditionOperator;
  value: string;
}

function seedFormConditions(rule: Rule): FormCondition[] {
  return rule.conditions.map((c) => ({
    field: FIELD_TO_BACKEND[c.type],
    operator: coerceOperator(c.operator),
    value: c.value,
  }));
}

function DragHandleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="3" r="1.2" />
      <circle cx="9" cy="3" r="1.2" />
      <circle cx="5" cy="7" r="1.2" />
      <circle cx="9" cy="7" r="1.2" />
      <circle cx="5" cy="11" r="1.2" />
      <circle cx="9" cy="11" r="1.2" />
    </svg>
  );
}

// Task 134 D2.7 — controlled child component. State lives in the parent
// (`RulePriorityRow`); this component is now a pure render of the
// `condition` prop with `onChange` callbacks per field.
function ExpansionConditionRow({
  condition,
  index,
  joiner,
  onChange,
}: {
  condition: FormCondition;
  index: number;
  /**
   * Controls the prefix rendered before every condition after the first.
   * `'any'` renders 'OR' (D-A from task 193); anything else renders 'AND'.
   * Mirrors the engine's mode default — historical rules without a stored
   * mode evaluate as AND, so the display defaults to AND.
   */
  joiner: 'all' | 'any';
  onChange: (
    index: number,
    next: Partial<{ field: FormConditionField; operator: FormConditionOperator; value: string }>,
  ) => void;
}) {
  const fieldId = useId();
  const opId = useId();
  const valId = useId();

  return (
    <div className={styles.expansionCondRow}>
      {index > 0 && <span className={styles.condPrefix}>{joiner === 'any' ? 'OR' : 'AND'}</span>}
      <label htmlFor={fieldId} className={styles.srOnly}>
        Field
      </label>
      <select
        id={fieldId}
        className={styles.condSelect}
        value={condition.field}
        onChange={(e) => onChange(index, { field: e.target.value as FormConditionField })}
      >
        <option value="app">App</option>
        <option value="title">Title</option>
        <option value="url">URL</option>
        <option value="site">Site</option>
        <option value="document">Document</option>
        <option value="ip">IP</option>
      </select>
      <label htmlFor={opId} className={styles.srOnly}>
        Operator
      </label>
      <select
        id={opId}
        className={styles.condSelect}
        value={condition.operator}
        onChange={(e) => onChange(index, { operator: e.target.value as FormConditionOperator })}
      >
        {/* Task 134 D7: parity with AddRulePanel. `starts with` and
            `ends with` are intentionally omitted — neither has a backend
            representation in matchConditionSchema. */}
        <option value="contains">contains</option>
        <option value="is">is</option>
        <option value="matches">matches</option>
      </select>
      <label htmlFor={valId} className={styles.srOnly}>
        Value
      </label>
      <input
        id={valId}
        type="text"
        className={styles.condInput}
        value={condition.value}
        onChange={(e) => onChange(index, { value: e.target.value })}
      />
    </div>
  );
}

export function RulePriorityRow({
  rule,
  projectName,
  projectArchived,
  clientName,
  editableProjects,
  isExpanded,
  onClick,
  onCloseExpansion,
}: {
  rule: Rule;
  projectName: string;
  /**
   * True when the rule's target project is archived (121c). Disabled-state
   * rules with an archived target get a "paused — target project archived"
   * caption next to the project name, and the project-column gains a muted
   * "Archived" suffix even for still-enabled rules (defensive — shouldn't
   * happen after 121b, but catches data drift).
   */
  projectArchived: boolean;
  clientName: string;
  /**
   * Task 134 D1 — non-archived, non-system-owned projects, threaded from
   * `ManageRulesPage`. The dropdown also defensively re-adds `rule.projectId`
   * itself if it's missing (archived target, or a system-owned project that
   * shouldn't normally appear), so the form never silently loses the current
   * selection.
   */
  editableProjects: readonly { id: string; name: string; archivedAt?: string | null }[];
  isExpanded: boolean;
  onClick: () => void;
  onCloseExpansion: () => void;
}) {
  const projectSelectId = useId();
  const enabledCheckId = useId();
  const nameInputId = useId();
  const priorityInputId = useId();

  const isDisabled = rule.status === 'disabled';
  const rowCls = [
    styles.ruleRow,
    isDisabled ? styles.disabledRow : '',
    isExpanded ? styles.expandedParent : '',
  ]
    .filter(Boolean)
    .join(' ');

  // ── Form state (D2) ──────────────────────────────────────────────────
  // One useState per field, plus an array for conditions. Seeded from the
  // `rule` prop on mount and re-seeded whenever the rule identity changes
  // — see the rule-id reset block below.
  const [name, setName] = useState(rule.name);
  const [priority, setPriority] = useState<number>(rule.priority);
  const [projectId, setProjectId] = useState(rule.projectId);
  const [enabled, setEnabled] = useState(rule.status === 'enabled');
  const [conditions, setConditions] = useState<FormCondition[]>(() => seedFormConditions(rule));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Confirm-dialog state. One mode at a time — Delete and Discard share
  // the primitive (D5 + D6). null means "no dialog open".
  const [confirmMode, setConfirmMode] = useState<'delete' | 'discard' | null>(null);

  const updateRule = useUpdateRule();
  const deleteRule = useDeleteRule();

  // Re-seed when the rule identity changes. The parent persists
  // `expandedRowId` but can expand a different row without unmounting
  // RulePriorityRow, so without a reset the previously-edited state
  // would carry into the new row. We use the React-recommended
  // "reset state during render via prevProps in state" pattern
  // (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // rather than useEffect, because effect-driven resets cascade an
  // extra render and run after the next render commits. Save success
  // closes the row (`onCloseExpansion()`), so an invalidation that
  // updates `rule` mid-edit only matters when the user explicitly
  // expands a different row — which is exactly when `rule.id` changes.
  const [prevRuleId, setPrevRuleId] = useState(rule.id);
  if (prevRuleId !== rule.id) {
    setPrevRuleId(rule.id);
    setName(rule.name);
    setPriority(rule.priority);
    setProjectId(rule.projectId);
    setEnabled(rule.status === 'enabled');
    setConditions(seedFormConditions(rule));
    setErrorMessage(null);
    setConfirmMode(null);
  }

  // ── Project dropdown — defensive fallback for archived / system-owned
  // current target. The threaded `editableProjects` list filters those
  // out, but the rule's actual target must always render so the form
  // doesn't silently lose the selection.
  const projectOptions = useMemo(() => {
    const list = editableProjects.map((p) => ({
      id: p.id,
      name: p.name,
      archived: Boolean(p.archivedAt),
    }));
    if (!list.some((p) => p.id === rule.projectId)) {
      list.push({
        id: rule.projectId,
        name: projectName,
        archived: projectArchived,
      });
    }
    return list;
  }, [editableProjects, rule.projectId, projectName, projectArchived]);

  // ── Dirty flag (D3) ──────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    if (name !== rule.name) return true;
    if (priority !== rule.priority) return true;
    if (projectId !== rule.projectId) return true;
    if (enabled !== (rule.status === 'enabled')) return true;
    if (conditions.length !== rule.conditions.length) return true;
    for (let i = 0; i < conditions.length; i++) {
      const a = conditions[i];
      const b = rule.conditions[i];
      if (!a || !b) return true;
      if (a.field !== FIELD_TO_BACKEND[b.type]) return true;
      if (a.operator !== coerceOperator(b.operator)) return true;
      if (a.value !== b.value) return true;
    }
    return false;
  }, [name, priority, projectId, enabled, conditions, rule]);

  const canSave =
    isDirty &&
    !updateRule.isPending &&
    name.trim() !== '' &&
    projectId !== '' &&
    conditions.every((c) => c.value.trim() !== '');

  const handleConditionChange = useCallback(
    (
      index: number,
      next: Partial<{ field: FormConditionField; operator: FormConditionOperator; value: string }>,
    ) => {
      setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...next } : c)));
    },
    [],
  );

  // ── Save (D4) ────────────────────────────────────────────────────────
  const handleSave = async () => {
    setErrorMessage(null);
    // Required-field gate. Save is already disabled when these fail,
    // but the explicit check guards against a future regression where
    // the disabled prop drifts and the click reaches here anyway.
    if (name.trim() === '') {
      setErrorMessage('Name is required.');
      return;
    }
    if (projectId === '') {
      setErrorMessage('Project is required.');
      return;
    }
    if (conditions.some((c) => c.value.trim() === '')) {
      setErrorMessage('Each condition must have a value.');
      return;
    }
    try {
      await updateRule.mutateAsync({
        id: rule.id,
        name: name.trim(),
        priority,
        projectId,
        enabled,
        match: {
          // Task 193 D-B: carry `mode` through the round-trip. Without
          // this, saving an "Any" rule rewrites it to `mode` undefined
          // (which the engine reads as 'all' / AND), silently flipping
          // the rule's evaluation. The inline-edit form does not yet
          // expose a mode toggle (task 134 territory) — this just
          // preserves whatever the rule arrived with.
          mode: rule.mode,
          conditions: conditions.map((c) => ({
            type: operatorToMatchType(c.operator),
            field: c.field,
            value: c.value.trim(),
          })),
        },
      });
      onCloseExpansion();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : 'Could not save rule — please try again.',
      );
    }
  };

  // ── Delete (D5) ──────────────────────────────────────────────────────
  const handleDeleteConfirmed = async () => {
    setErrorMessage(null);
    setConfirmMode(null);
    try {
      await deleteRule.mutateAsync(rule.id);
      // The row disappears on the next React Query settle — this call is
      // defensive so the parent's expansion state clears even if the
      // invalidation lags.
      onCloseExpansion();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : 'Could not delete rule — please try again.',
      );
    }
  };

  // ── Cancel + dirty guard (D6) ───────────────────────────────────────
  const handleCancel = () => {
    if (isDirty) {
      setConfirmMode('discard');
      return;
    }
    onCloseExpansion();
  };

  const handleDiscardConfirmed = () => {
    setConfirmMode(null);
    setErrorMessage(null);
    onCloseExpansion();
  };

  return (
    <>
      <tr className={rowCls} onClick={onClick}>
        <td className={styles.dragCell}>
          <span className={styles.dragHandle}>
            <DragHandleIcon />
          </span>
        </td>
        <td>
          <div className={styles.priorityCell}>
            #{rule.priority}
            <StatusDot tone={rule.status === 'enabled' ? 'enabled' : 'disabled'} />
          </div>
        </td>
        <td>
          <div className={styles.conditionsCell}>
            {rule.conditions.map((c, i) => (
              // Task 193 D-A — emit a joiner badge before every condition
              // after the first so the displayed semantics match the
              // stored `mode`. Wrapped in a Fragment because the cell
              // already lays badges out side-by-side; we want the joiner
              // in document order, not as a sibling group.
              <span key={`${rule.id}-cond-${i}`} className={styles.condGroup}>
                {i > 0 && (
                  <span className={styles.condPrefix}>{rule.mode === 'any' ? 'OR' : 'AND'}</span>
                )}
                <ConditionBadge type={c.type} operator={c.operator} value={c.value} size="sm" />
              </span>
            ))}
          </div>
        </td>
        <td className={styles.projectCol}>
          {projectName}
          {projectArchived ? (
            <span className={styles.archivedBadge} title="Target project is archived">
              {' '}
              &middot; Archived
            </span>
          ) : null}
          {isDisabled && projectArchived ? (
            <div className={styles.pausedReason}>Paused &mdash; target project archived.</div>
          ) : null}
        </td>
        <td
          className={`${styles.clientCol} ${clientName === '— (General)' ? styles.clientGeneral : ''}`}
        >
          {clientName}
        </td>
        <td className={styles.createdCol}>{rule.name}</td>
        <td className={styles.eventsCol}>
          <span className={styles.eventsZero}>&mdash;</span>
        </td>
      </tr>
      {isExpanded && (
        <tr className={styles.expansionRow}>
          <td colSpan={7}>
            <div className={styles.expansionContent}>
              <div className={styles.expansionForm}>
                {errorMessage && (
                  <div className={styles.expansionError}>
                    <InfoBanner tone="danger">{errorMessage}</InfoBanner>
                  </div>
                )}
                <div className={styles.expansionField}>
                  <label htmlFor={nameInputId} className={styles.expansionLabel}>
                    Name
                  </label>
                  <input
                    id={nameInputId}
                    type="text"
                    className={styles.expansionInput}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className={styles.expansionField}>
                  <label htmlFor={priorityInputId} className={styles.expansionLabel}>
                    Priority
                  </label>
                  <input
                    id={priorityInputId}
                    type="number"
                    className={styles.expansionPriorityInput}
                    value={priority}
                    onChange={(e) => {
                      const parsed = Number(e.target.value);
                      if (Number.isFinite(parsed)) setPriority(parsed);
                    }}
                  />
                </div>
                <div className={styles.expansionField}>
                  <label htmlFor={projectSelectId} className={styles.expansionLabel}>
                    Project
                  </label>
                  <select
                    id={projectSelectId}
                    className={styles.panelSelect}
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                  >
                    {projectOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.archived ? ' (Archived)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.expansionField}>
                  <label htmlFor={enabledCheckId} className={styles.expansionLabel}>
                    Enabled
                  </label>
                  <div className={styles.expansionCheckbox}>
                    <input
                      id={enabledCheckId}
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                    />
                    <span>Rule is enabled</span>
                  </div>
                </div>
                <div className={`${styles.expansionField} ${styles.fullWidth}`}>
                  <div className={styles.expansionCondLabel}>Match Conditions</div>
                  {conditions.map((c, i) => (
                    <ExpansionConditionRow
                      key={`exp-${rule.id}-${i}`}
                      condition={c}
                      index={i}
                      joiner={rule.mode}
                      onChange={handleConditionChange}
                    />
                  ))}
                </div>
                <div className={styles.expansionActions}>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={updateRule.isPending || deleteRule.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      void handleSave();
                    }}
                    disabled={!canSave}
                  >
                    {updateRule.isPending ? 'Saving…' : 'Save Changes'}
                  </Button>
                  <button
                    type="button"
                    className={styles.btnDanger}
                    onClick={() => setConfirmMode('delete')}
                    disabled={updateRule.isPending || deleteRule.isPending}
                  >
                    {deleteRule.isPending ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {confirmMode === 'delete' && (
        <tr>
          <td colSpan={7} style={{ padding: 0, border: 0 }}>
            <div
              className={styles.confirmOverlay}
              role="dialog"
              aria-modal="true"
              aria-labelledby={`${rule.id}-delete-confirm-title`}
            >
              <div className={styles.confirmPanel}>
                <h3 id={`${rule.id}-delete-confirm-title`} className={styles.confirmTitle}>
                  Delete rule?
                </h3>
                <p className={styles.confirmText}>
                  This action cannot be undone. &ldquo;<strong>{rule.name}</strong>&rdquo; will be
                  removed and will no longer classify new events.
                </p>
                <div className={styles.confirmActions}>
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={() => setConfirmMode(null)}
                    disabled={deleteRule.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    type="button"
                    onClick={() => {
                      void handleDeleteConfirmed();
                    }}
                    disabled={deleteRule.isPending}
                  >
                    {deleteRule.isPending ? 'Deleting…' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {confirmMode === 'discard' && (
        <tr>
          <td colSpan={7} style={{ padding: 0, border: 0 }}>
            <div
              className={styles.confirmOverlay}
              role="dialog"
              aria-modal="true"
              aria-labelledby={`${rule.id}-discard-confirm-title`}
            >
              <div className={styles.confirmPanel}>
                <h3 id={`${rule.id}-discard-confirm-title`} className={styles.confirmTitle}>
                  Discard unsaved changes to this rule?
                </h3>
                <p className={styles.confirmText}>
                  Your edits to &ldquo;<strong>{rule.name}</strong>&rdquo; have not been saved.
                  Discarding will revert to the saved version.
                </p>
                <div className={styles.confirmActions}>
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={() => setConfirmMode(null)}
                  >
                    Keep editing
                  </Button>
                  <Button variant="danger" size="sm" type="button" onClick={handleDiscardConfirmed}>
                    Discard
                  </Button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
